import { Flex, Spacer, VStack } from '@chakra-ui/react'
import { RightArrowIcon } from '@components/icons/RightArrowIcon'
import { Body, Button, Heading } from '@gaia-family/ds'
import { FormikValues, useFormikContext } from 'formik'
import { useEffect } from 'react'

import { QuestionField } from './QuestionField'
import { signUpQuestions } from './utils/screenDefinitions/signUpQuestions'
import { SignUpScreenId } from './utils/screenDefinitions/signUpScreens'
import { getPreviousScreenId, isFirstScreen } from './utils/screenUtils'
import { SignUpScreen } from './utils/types'

export const ScreenTemplate = ({
  screen,
  currentScreenId,
  setCurrentScreenId,
  isSubmitting,
  setSubmitting,
  values,
}: {
  screen: SignUpScreen
  currentScreenId: SignUpScreenId
  setCurrentScreenId: (nextScreenId: SignUpScreenId) => void
  isSubmitting: boolean
  setSubmitting: (isSubmitting: boolean) => void
  values: FormikValues
}) => {
  const { title, description, buttonText, questions, backText } = screen

  // reset isSubmitting to false when get to next screen
  // see https://formik.org/docs/guides/form-submission#submission - we have to do this because handleNext is not promise based
  useEffect(() => {
    setSubmitting(false)
  }, [setSubmitting, currentScreenId])

  const { setFieldValue } = useFormikContext()

  const handleBack = () => {
    // reset this screens value(s)
    questions.forEach((questionId) => {
      setFieldValue(questionId, undefined)
    })

    // calculate previous screen
    const previousScreen = getPreviousScreenId(values, screen.questions)
    if (previousScreen) setCurrentScreenId(previousScreen)
  }

  const showBackButton = !isFirstScreen(questions)

  return (
    <Flex direction="column" height="100%">
      <VStack alignItems="flex-start" width="100%">
        {showBackButton && (
          <Button
            onClick={handleBack}
            variant="unstyled"
            leftIcon={
              <RightArrowIcon width="25px" height="25px" fill="black" />
            }
          >
            {backText}
          </Button>
        )}
        {title && <Heading>{title}</Heading>}
        <Body>{description}</Body>
        {questions.map((questionId) => (
          <QuestionField
            question={signUpQuestions[questionId]}
            key={questionId}
            questionId={questionId}
          />
        ))}
      </VStack>
      <Spacer flex={[1, 0]} />
      <Button
        type="submit"
        // todo: disable until question(s) answered
        isDisabled={isSubmitting}
        variant="secondary"
        width={['100%', 'fit-content']}
      >
        {buttonText}
      </Button>
    </Flex>
  )
}
